/**
 * Safe localStorage/sessionStorage wrappers that handle quota errors and other
 * storage failures gracefully. Use these instead of direct storage access to
 * prevent uncaught exceptions from breaking UI initialization.
 */

export type SetResult =
  | { ok: true }
  | { ok: false; reason: "unavailable" | "quota" | "too_large" | "unknown" };

export type EvictionStrategy = "oldestByTimestamp" | "oldestByKeyOrder";

export interface SetOptions {
  /** Maximum allowed byte size for this value. If exceeded, returns { ok: false, reason: 'too_large' }. */
  maxBytes?: number;
  /** Optional eviction config to run when quota is exceeded. */
  evict?: {
    /** Key prefix to search for evictable entries (e.g. 'blacksmith_workflow_cache_'). */
    prefix: string;
    /** Key to exclude from eviction (usually the key being written). */
    exceptKey?: string;
    /** Eviction strategy. 'oldestByTimestamp' requires getTimestamp. */
    strategy: EvictionStrategy;
  };
  /** Extract a timestamp from a stored value for 'oldestByTimestamp' eviction. */
  getTimestamp?: (rawValue: string) => number;
}

/**
 * Checks if an error is a QuotaExceededError (storage full).
 */
export function isQuotaExceededError(err: unknown): boolean {
  if (err instanceof DOMException) {
    // Most browsers use name "QuotaExceededError", but some older ones use code 22.
    return err.name === "QuotaExceededError" || err.code === 22;
  }
  return false;
}

/**
 * Checks if storage is available (e.g. not disabled, in private mode, etc.).
 */
function isStorageAvailable(storage: Storage): boolean {
  try {
    const testKey = "__storage_test__";
    storage.setItem(testKey, "1");
    storage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

/**
 * Find all keys in storage matching a prefix.
 */
function getKeysWithPrefix(storage: Storage, prefix: string): string[] {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key?.startsWith(prefix)) {
      keys.push(key);
    }
  }
  return keys;
}

/**
 * Evict entries by prefix using the specified strategy.
 * Returns true if at least one entry was evicted.
 */
function evictByPrefix(
  storage: Storage,
  prefix: string,
  exceptKey: string | undefined,
  strategy: EvictionStrategy,
  getTimestamp?: (rawValue: string) => number,
): boolean {
  const keys = getKeysWithPrefix(storage, prefix).filter(
    (k) => k !== exceptKey,
  );
  if (keys.length === 0) return false;

  if (strategy === "oldestByTimestamp" && getTimestamp) {
    // Sort by timestamp ascending (oldest first)
    const keysWithTs = keys
      .map((key) => {
        const raw = storage.getItem(key);
        if (!raw) return null;
        try {
          return { key, ts: getTimestamp(raw) };
        } catch {
          // If we can't parse timestamp, treat as very old
          return { key, ts: 0 };
        }
      })
      .filter((x): x is { key: string; ts: number } => x !== null)
      .sort((a, b) => a.ts - b.ts);

    if (keysWithTs.length > 0) {
      storage.removeItem(keysWithTs[0].key);
      return true;
    }
  } else {
    // oldestByKeyOrder: just remove the first one we found
    storage.removeItem(keys[0]);
    return true;
  }

  return false;
}

// Only access window.localStorage/sessionStorage in browser environment
function getLocalStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function getSessionStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Create a safe storage wrapper that lazily accesses the storage on each call.
 * This ensures it works correctly during SSR and after hydration.
 */
function createSafeStorage(getStorage: () => Storage | null) {
  return {
    /**
     * Safely get a value from storage.
     * Returns null if storage is unavailable or key doesn't exist.
     */
    get(key: string): string | null {
      const storage = getStorage();
      if (!storage) return null;
      try {
        return storage.getItem(key);
      } catch {
        return null;
      }
    },

    /**
     * Safely set a value in storage.
     * Returns { ok: true } on success, or { ok: false, reason } on failure.
     */
    set(key: string, value: string, opts?: SetOptions): SetResult {
      const storage = getStorage();
      if (!storage || !isStorageAvailable(storage)) {
        return { ok: false, reason: "unavailable" };
      }

      // Check max bytes limit
      if (opts?.maxBytes !== undefined) {
        const byteSize = new Blob([value]).size;
        if (byteSize > opts.maxBytes) {
          return { ok: false, reason: "too_large" };
        }
      }

      try {
        storage.setItem(key, value);
        return { ok: true };
      } catch (err) {
        if (isQuotaExceededError(err)) {
          // Attempt eviction if configured
          if (opts?.evict) {
            const evicted = evictByPrefix(
              storage,
              opts.evict.prefix,
              opts.evict.exceptKey ?? key,
              opts.evict.strategy,
              opts.getTimestamp,
            );
            if (evicted) {
              // Retry once after eviction
              try {
                storage.setItem(key, value);
                return { ok: true };
              } catch (retryErr) {
                if (isQuotaExceededError(retryErr)) {
                  return { ok: false, reason: "quota" };
                }
                return { ok: false, reason: "unknown" };
              }
            }
          }
          return { ok: false, reason: "quota" };
        }
        return { ok: false, reason: "unknown" };
      }
    },

    /**
     * Safely remove a key from storage.
     * Silently fails if storage is unavailable.
     */
    remove(key: string): void {
      const storage = getStorage();
      if (!storage) return;
      try {
        storage.removeItem(key);
      } catch {
        // Silently ignore removal failures
      }
    },

    /**
     * Get all keys matching a prefix.
     * Returns empty array if storage is unavailable.
     */
    getKeysWithPrefix(prefix: string): string[] {
      const storage = getStorage();
      if (!storage) return [];
      try {
        return getKeysWithPrefix(storage, prefix);
      } catch {
        return [];
      }
    },

    /**
     * Clear all entries matching a prefix.
     */
    clearByPrefix(prefix: string): void {
      const storage = getStorage();
      if (!storage) return;
      try {
        const keys = getKeysWithPrefix(storage, prefix);
        for (const key of keys) {
          storage.removeItem(key);
        }
      } catch {
        // Silently ignore
      }
    },
  };
}

/**
 * Safe localStorage wrapper. All methods handle errors gracefully.
 */
export const safeLocalStorage = createSafeStorage(getLocalStorage);

/**
 * Safe sessionStorage wrapper. All methods handle errors gracefully.
 */
export const safeSessionStorage = createSafeStorage(getSessionStorage);
